import { forwardRef, useEffect, useRef, useState } from "react";
import styles from "../../../pages/ordermanager/Orders.module.css";
import { CloseIcon, TemplateNotFound } from "../../icons/Icon2";
import SlidingFilter2 from "../Dealers/SlidingFilter2";
import NoDataFound from "../NoDataFound/NoDataFound";
import { Dispatch } from "../../icons/Icon";

const ShippingChargesInfoModal = forwardRef(
  (
    {
      mode,
      handleCloseModal,
      shippingType,
      itemDetails,
      shippingInfo,
      setShippingInfo,
      dw,
    },
    ref,
  ) => {
    /* -------------------- Warehouses -------------------- */
    const uniqueWarehouses = itemDetails
      .filter((i) => i.warehouse && i.warehouseId)
      .reduce((acc, item) => {
        if (!acc.find((w) => w.warehouseId === item.warehouseId)) {
          acc.push({
            warehouse: item.warehouse,
            warehouseId: item.warehouseId,
          });
        }
        return acc;
      }, []);

    const warehouseDisplayList = uniqueWarehouses.map((w) => ({
      id: w.warehouseId,
      label: w.warehouse,
    }));

    const getWarehouseIndexById = (id) =>
      uniqueWarehouses.findIndex((w) => w.warehouseId === id);

    /* -------------------- State -------------------- */
    const [selectedWarehouse, setSelectedWarehouse] = useState(() => {
      const first = uniqueWarehouses[0];
      return first
        ? { label: first.warehouse, value: first.warehouseId }
        : { label: "", value: "" };
    });

    const [inputValue, setInputValue] = useState("");
    const [isSelfMethod, setIsSelfMethod] = useState(false);
    const inputRef = useRef(null);

    /* -------------------- Effects -------------------- */
    useEffect(() => {
      inputRef.current?.focus();
    }, [selectedWarehouse]);

    useEffect(() => {
      const data =
        shippingType === "Combined"
          ? shippingInfo.find((w) => w.isDispatchWarehouse)
          : shippingInfo.find((w) => w.warehouseId === selectedWarehouse.value);

      setIsSelfMethod(data?.methodName === "Self");
      setInputValue(data?.shippingCharge ?? "");
    }, [shippingType, selectedWarehouse, shippingInfo]);

    /* -------------------- Handlers -------------------- */
    const warehouseChange = (warehouseId) => {
      const w = uniqueWarehouses.find((x) => x.warehouseId === warehouseId);
      if (!w) return;

      setSelectedWarehouse({ label: w.warehouse, value: w.warehouseId });

      const data = shippingInfo.find((s) => s.warehouseId === warehouseId);
      setIsSelfMethod(data?.methodName === "Self");
      setInputValue(data?.shippingCharge ?? "");
    };

    // const handleChange = (e) => {
    //   if (isSelfMethod) return;

    //   let value = Math.max(0, Number(e.target.value || 0));
    //   setInputValue(value);

    //   setShippingInfo((prev) =>
    //     prev.map((item) =>
    //       item.warehouseId ===
    //       (shippingType === "Combined" ? dw.value : selectedWarehouse.value)
    //         ? { ...item, shippingCharge: value }
    //         : item,
    //     ),
    //   );
    // };

    const handleChange = (e) => {
      if (isSelfMethod) return;

      const rawValue = e.target.value;

      // If input is empty, default to 0, otherwise parse as Base-10 Integer
      // parseInt("050", 10) returns 50
      const value = rawValue === "" ? 0 : parseInt(rawValue, 10);

      // Ensure value is not negative
      const finalValue = Math.max(0, value);

      setInputValue(finalValue);

      setShippingInfo((prev) =>
        prev.map((item) =>
          item.warehouseId ===
          (shippingType === "Combined" ? dw.value : selectedWarehouse.value)
            ? { ...item, shippingCharge: finalValue }
            : item,
        ),
      );
    };

    // const handleKeyDown = (e) => {
    //   if (e.key !== "Enter") return;

    //   const currentIndex = getWarehouseIndexById(selectedWarehouse.value);

    //   if (currentIndex < uniqueWarehouses.length - 1) {
    //     const next = uniqueWarehouses[currentIndex + 1];
    //     warehouseChange(next.warehouseId);
    //   } else {
    //     handleCloseModal();
    //     requestAnimationFrame(() => ref?.current?.focus());
    //   }
    // };

    const handleKeyDown = (e) => {
      if (e.key !== "Enter") return;

      if (shippingType === "Combined") {
        handleCloseModal();
        requestAnimationFrame(() => ref?.current?.focus());
        return;
      }

      const currentIndex = getWarehouseIndexById(selectedWarehouse.value);

      if (currentIndex < uniqueWarehouses.length - 1) {
        const next = uniqueWarehouses[currentIndex + 1];
        warehouseChange(next.warehouseId);
      } else {
        handleCloseModal();
        requestAnimationFrame(() => ref?.current?.focus());
      }
    };

    const isValidItem = (item) => {
      return !(
        item.amount === 0 &&
        item.price === 0 &&
        item.qty === 0 &&
        item.dp === 0 &&
        item.vm === 0 &&
        item.weight === 0 &&
        item.totalQty === "" &&
        item.product_name === "" &&
        item.itemNo === "" &&
        item.collection_name === ""
      );
    };

    /* -------------------- Calculations -------------------- */
    const filteredItems =
      shippingType === "Separate"
        ? itemDetails.filter(
            (i) => i.warehouseId === selectedWarehouse.value && isValidItem(i),
          )
        : itemDetails.filter(isValidItem);

    const totalCBM = filteredItems.reduce(
      (s, i) => s + Number(i.vm || 0) * i.qty,
      0,
    );

    const totalWeight = filteredItems.reduce(
      (s, i) => s + Number(i.weight || 0) * i.qty,
      0,
    );

    /* -------------------- Styles -------------------- */
    const liStyles = {
      backgroundColor: mode ? "#2C2E33" : "#D9DCE5",
      color: mode ? "#FFFFFF" : "#646B88",
      fontWeight: "600",
    };

    const IPSLiStyles = {
      color: mode ? "#FFFFFF" : "#646B88",
      backgroundColor: mode ? "#1B1D21" : "#FFFFFF",
    };

    /* -------------------- UI -------------------- */
    return (
      <div
        className={styles.sci_modal}
        style={{ backgroundColor: mode && "#232529" }}
      >
        <div className={styles.modal_header}>
          <p style={{ color: mode && "#fff" }}>Shipping Charges Info</p>

          {shippingType === "Separate" && (
            <SlidingFilter2
              itemList={warehouseDisplayList.map((w) => w.label)}
              selectedItem={selectedWarehouse.label}
              setFilter={(label) => {
                const found = warehouseDisplayList.find(
                  (w) => w.label === label,
                );
                found && warehouseChange(found.id);
              }}
              enableKeyboardNavigation
            />
          )}

          <div onClick={handleCloseModal}>
            <CloseIcon mode={mode} />
          </div>
        </div>

        {!itemDetails.length ? (
          <NoDataFound
            icon={<TemplateNotFound />}
            textTitle="No items to calculate shipping charges"
          />
        ) : (
          <>
            <ul className={styles.sc_data}>
              <li style={liStyles}>
                <div>Item</div>
                <div>Warehouse</div>
                <div>Quantity</div>
                <div>Volume(CBM)</div>
                <div>Weight(KG)</div>
                <div>Shipping Charge</div>
              </li>

              <div className={styles.sc_data_scroll}>
                {filteredItems.map((item, i) => (
                  <li style={IPSLiStyles} key={i}>
                    <div>{item.itemNo}</div>
                    <div>
                      {item.warehouse}
                      {item.warehouseId === dw.value && <Dispatch />}
                    </div>
                    <div>{item.qty}</div>
                    <div>{item.vm}</div>
                    <div>{item.weight}</div>
                    <div></div>
                  </li>
                ))}
              </div>

              <li style={liStyles}>
                <div>Total</div>
                <div />
                <div />
                <div>{totalCBM.toFixed(2)}</div>
                <div>{totalWeight.toFixed(2)}</div>
                <div>
                  {/* <input
                    ref={inputRef}
                    type="number"
                    value={inputValue}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    disabled={isSelfMethod}
                    className={styles.sc_inp}
                    style={{ color: mode && "#FFFFFF" }}
                    onFocus={(e) => e.target.select()}
                  /> */}
                  <input
                    ref={inputRef}
                    type="number"
                    value={Number(inputValue).toString()}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    disabled={isSelfMethod}
                    className={styles.sc_inp}
                    style={{ color: mode && "#FFFFFF" }}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
              </li>
            </ul>
          </>
        )}
      </div>
    );
  },
);

export default ShippingChargesInfoModal;
